import type { InternalAxiosRequestConfig } from 'axios';
import type { WidgetModel } from '../services/DashboardsService';
import { dashboardMockupData } from './dashboardMockupData';

const WIDGET_API_PREFIX = 'admin/store/';

function isDashboardLayoutRoute()
{
    return /\/dashboards\/\d+\/layout\/?$/i.test(window.location.pathname);
}

export function isDashboardLayoutMockupPreview()
{
    if (import.meta.server)
    {
        return false;
    }

    return isDashboardLayoutRoute();
}

export function isDashboardMockupEnabled()
{
    if (import.meta.server)
    {
        return false;
    }

    const params = new URLSearchParams(window.location.search);

    if (params.has('mockup'))
    {
        return true;
    }

    return isDashboardLayoutRoute();
}

export function getMockupWidgetPreviewColor(widgetType: string)
{
    if (!isDashboardMockupEnabled())
    {
        return undefined;
    }

    return MOCKUP_WIDGET_PREVIEW_COLORS[widgetType];
}

export function normalizeDashboardMockupUrl(config: InternalAxiosRequestConfig)
{
    const rawUrl = config.url ?? '';
    const baseUrl = config.baseURL ?? '';
    let path = rawUrl.startsWith('http')
        ? rawUrl
        : `${baseUrl.replace(/\/$/, '')}/${rawUrl.replace(/^\//, '')}`;

    path = path.replace(/^https?:\/\/[^/]+/i, '');
    path = path.replace(/^\/api\//i, '');
    path = path.replace(/^\//, '');
    path = path.split('?')[0];

    return path.toLowerCase();
}

export function resolveDashboardMockupResponse(config: InternalAxiosRequestConfig): unknown | undefined
{
    const path = normalizeDashboardMockupUrl(config);

    if (!path.startsWith(WIDGET_API_PREFIX))
    {
        return undefined;
    }

    const params = config.params ?? {};

    switch (path)
    {
        case 'admin/store/orders/widgets/total-count':
            return dashboardMockupData.ordersTotalCount;
        case 'admin/store/orders/widgets/total-value':
            return dashboardMockupData.ordersTotalValue;
        case 'admin/store/orders/widgets/average-value':
            return dashboardMockupData.ordersAverageValue;
        case 'admin/store/orders/widgets/sales-statistics':
            return dashboardMockupData.salesStatistics;
        case 'admin/store/orders/widgets/cities':
            return sliceByLimit(
                [...dashboardMockupData.ordersByCities].sort((first, second) => second.value - first.value),
                5
            );
        case 'admin/store/orders/widgets/payments-count':
            return dashboardMockupData.ordersPaymentsCount;
        case 'admin/store/orders/widgets/deliveries-count':
            return dashboardMockupData.ordersDeliveriesCount;
        case 'admin/store/orders/widgets/latest':
            return filterLatestOrders(params);
        case 'admin/store/orders/widgets/best-paying-customers':
            return sliceByLimit(dashboardMockupData.bestPayingCustomers, params.limit);
        case 'admin/store/orders/widgets/promotion-codes-effectiveness':
            return dashboardMockupData.promotionCodesEffectiveness;
        case 'admin/store/shops/widgets/sessions-count':
            return dashboardMockupData.sessionsCount;
        case 'admin/store/shops/widgets/conversion-rate':
            return dashboardMockupData.conversionRate;
        case 'admin/store/shops/widgets/sessions-chart':
            return dashboardMockupData.sessionsChart;
        case 'admin/store/shops/widgets/sessions-by-device-chart':
            return dashboardMockupData.sessionsByDevice;
        case 'admin/store/shops/widgets/new-vs-returning-users':
            return dashboardMockupData.newVsReturningUsers;
        case 'admin/store/products/widgets/top-selling':
            return sliceByLimit(dashboardMockupData.topSellingProducts, params.limit);
        case 'admin/store/products/widgets/top-selling-variants':
            return sliceByLimit(dashboardMockupData.topSellingVariants, params.limit);
        case 'admin/store/products/widgets/most-complained':
            return sliceByLimit(dashboardMockupData.mostComplainedProducts, params.limit);
        case 'admin/store/products/widgets/most-abandoned':
            return sliceByLimit(dashboardMockupData.mostAbandonedProducts, params.limit);
        case 'admin/store/products/widgets/low-stock':
            return dashboardMockupData.lowStockProducts;
        case 'admin/store/complaints/widgets/rate':
            return { value: dashboardMockupData.complaintRate.value };
        case 'admin/store/complaints/widgets/total-count':
            return params.status
                ? dashboardMockupData.complaintsPendingCount
                : dashboardMockupData.complaintsTotalCount;
        case 'admin/store/complaints/widgets/reasons':
            return [
                { reason: 'DamagedAfterDelivery', count: 38 },
                { reason: 'ManufacturingDefect', count: 27 },
                { reason: 'NotAsDescribed', count: 19 },
                { reason: 'MissingPartsOrComponents', count: 14 },
                { reason: 'Other', count: 8 }
            ];
        case 'admin/store/complaints/widgets/handling-time':
            return dashboardMockupData.complaintHandlingTime;
        case 'admin/store/returns/widgets/rate':
            return { value: dashboardMockupData.returnRate.value };
        case 'admin/store/returns/widgets/total-count':
            return params.status
                ? dashboardMockupData.returnsPendingCount
                : dashboardMockupData.returnsTotalCount;
        case 'admin/store/returns/widgets/reasons':
            return [
                { reason: 'SizeDoesNotFit', count: 52 },
                { reason: 'ProductDoesNotMeetExpectations', count: 34 },
                { reason: 'OrderedByMistake', count: 28 },
                { reason: 'ProductDiffersFromPhotos', count: 11 },
                { reason: 'Other', count: 6 }
            ];
        case 'admin/store/returns/widgets/handling-time':
            return dashboardMockupData.returnHandlingTime;
        default:
            break;
    }

    const originMatch = path.match(/^admin\/store\/orders\/widgets\/(source|medium|campaign)\/origin-chart-data$/);

    if (originMatch)
    {
        const utmType = originMatch[1];

        if (utmType === 'medium')
        {
            return dashboardMockupData.ordersByMedium;
        }

        if (utmType === 'source')
        {
            return dashboardMockupData.ordersBySource;
        }

        return dashboardMockupData.ordersByCampaign;
    }

    return undefined;
}

const MOCKUP_WIDGET_PREVIEW_COLORS: Record<string, string> = {
    'complaint-rate': dashboardMockupData.complaintRate.backgroundColor,
    'return-rate': dashboardMockupData.returnRate.backgroundColor
};

export function applyMockupWidgetPreviewColors(widgets: WidgetModel[]): WidgetModel[]
{
    if (!isDashboardMockupEnabled())
    {
        return widgets;
    }

    return widgets.map((widget) =>
    {
        const previewColor = MOCKUP_WIDGET_PREVIEW_COLORS[widget.type];

        if (!previewColor || widget.backgroundColor)
        {
            return widget;
        }

        return {
            ...widget,
            backgroundColor: previewColor
        };
    });
}

function sliceByLimit<T>(items: T[], limit?: number)
{
    if (!limit || limit >= items.length)
    {
        return items;
    }

    return items.slice(0, Number(limit));
}

function filterLatestOrders(params: Record<string, unknown>)
{
    let items = dashboardMockupData.latestOrders;

    if (params.status)
    {
        items = items.filter(item => item.status === params.status);
    }

    return sliceByLimit(items, params.limit as number | undefined);
}
